import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, CheckCircle2, Image as ImageIcon, Paperclip, X } from 'lucide-react';
import { Card } from '@shared/ui/containers/Card';
import { Button } from '@shared/ui/primitives/Button';
import { Dropdown } from '@shared/ui/primitives/Dropdown';
import { ActionButton } from '@shared/ui/forms/FormFields';
import { SignatureField } from '@shared/ui/forms/SignatureField';
import { Stepper } from '@shared/ui/lists/Stepper';
import { CompactHeader } from '@shared/ui/headers/HeroHeader';
import { Banner } from '@shared/ui/feedback/Banner';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { toApiError } from '@shared/api/errors';
import { setJoinTenantContext } from '@shared/api/joinTenantContext';
import { toast } from '@shared/ui/feedback/Toast';
import {
  activateStep,
  completeInstance,
  completeStep,
  createFlowInstance,
  getFlowInstance,
  moveToPreviousStep,
  requestFlowOtp,
  updateItemValue,
  uploadFlowFiles,
  verifyFlowOtp,
} from './api';
import type { FlowInstance, FlowInstanceStep, FlowInstanceStepItem } from './types';

type Config = Record<string, unknown>;

const cfg = (item: FlowInstanceStepItem): Config => (item.item_config ?? {}) as Config;
const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

/**
 * Flatten section children into a single ordered list. Questions are nested under
 * their `section` item, so the runner must expand them to render, validate, and
 * save their values (otherwise required questions never appear or submit).
 */
function flattenItems(items: FlowInstanceStepItem[]): FlowInstanceStepItem[] {
  const out: FlowInstanceStepItem[] = [];
  for (const item of items) {
    out.push(item);
    if (item.children?.length) out.push(...flattenItems(item.children));
  }
  return out;
}

/**
 * Server-driven onboarding runner — flow-instance model (matches the live
 * Angular `OnboardingComponent`). Every step item is rendered by type:
 *   - content    → rich text / HTML block
 *   - question   → a valued input (text, select, checkbox, file, …)
 *   - action     → an interactive handler (e.g. OTP verification)
 *   - transition → the button that completes the step and advances the flow
 *
 * Route shapes:
 *   /onboarding/:id              → :id is a flow_template_id (create instance)
 *   /onboarding?template=…       → flow_template_id via query
 *   /onboarding?instance=…       → resume an existing instance
 *   /onboarding?onboardingId=…   → link an existing tenant onboarding row
 *
 * Embedded mode (access invite onboarding):
 *   embedInstanceId + onEmbedComplete — stay on parent page and finalize credentials there.
 */
export interface OnboardingEmbedProps {
  embedInstanceId?: string;
  onEmbedComplete?: () => void;
  /** Drives the chrome/labels. 'survey' is used by the dedicated SurveyRunner. */
  variant?: 'onboarding' | 'survey';
}

export function FlowRunner({ embedInstanceId, onEmbedComplete, variant = 'onboarding' }: OnboardingEmbedProps = {}): JSX.Element {
  const navigate = useNavigate();
  const { id: paramId } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const queryClient = useQueryClient();
  const embedCompleteFired = useRef(false);

  const instanceId = embedInstanceId ?? search.get('instance') ?? undefined;
  const flowTemplateId = paramId ?? search.get('template') ?? undefined;
  const onboardingId = search.get('onboardingId') ?? undefined;
  const flowContext = search.get('context') ?? undefined;
  const flowKind = search.get('kind') ?? undefined;
  const flowTitle = search.get('title') ?? undefined;
  const flowBackTo = search.get('backTo') ?? undefined;
  // Where to land after the survey/eForm is completed (the member's submission results).
  const flowResultsTo = search.get('resultsTo') ?? undefined;
  // When launched from the "join another community" flow, keep addressing the
  // target tenant (X-Tenant-ID) for every flow-instance call. Set synchronously
  // so it's active before react-query fires; cleared on unmount.
  const joinTenantId = search.get('joinTenant') ?? undefined;
  if (joinTenantId) setJoinTenantContext(joinTenantId);
  useEffect(() => {
    return () => {
      if (joinTenantId) setJoinTenantContext(null);
    };
  }, [joinTenantId]);

  /* Leave the flow — drop the join-tenant override and return to the app.
     (For a post-login required-onboarding gate the gate may route the member
     back; for the join flow it returns them to their own community.) */
  const handleExit = () => {
    if (joinTenantId) setJoinTenantContext(null);
    navigate('/app/home');
  };
  const isSurveyFlow = variant === 'survey' || flowContext === 'survey';
  const handleSurveyBack = () => {
    if (joinTenantId) setJoinTenantContext(null);
    const idx = typeof window !== 'undefined' ? window.history.state?.idx : undefined;
    if (typeof idx === 'number' && idx > 0) {
      navigate(-1);
      return;
    }
    navigate(flowBackTo || '/app/surveys');
  };
  const queryKey = ['flow-instance', instanceId ?? flowTemplateId ?? 'default', onboardingId];

  const [values, setValues] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);

  const instanceQuery = useQuery<FlowInstance>({
    queryKey,
    queryFn: async () => {
      if (instanceId) return getFlowInstance(instanceId);
      return createFlowInstance({ flow_template_id: flowTemplateId, onboarding_id: onboardingId });
    },
  });

  const instance = instanceQuery.data;
  const steps = useMemo(() => instance?.steps ?? [], [instance]);
  const activeStep = useMemo<FlowInstanceStep | undefined>(
    () => steps.find((s) => s.status === 'active') ?? steps.find((s) => s.status === 'pending'),
    [steps],
  );
  const stepIndex = activeStep ? steps.indexOf(activeStep) : steps.length;
  const done = instance?.status === 'completed';
  // Section children (questions) are nested — flatten so every read path sees them.
  const flatItems = useMemo(() => flattenItems(activeStep?.items ?? []), [activeStep]);

  // content_key → answered value across all steps, for interpolating {{placeholders}}
  // in content blocks (e.g. the Review and confirmation steps). The server snapshot is
  // authoritative; item values fill any gaps.
  const contentValues = useMemo(() => {
    const map: Record<string, unknown> = {
      ...((instance?.instance_snapshot as Record<string, unknown> | undefined) ?? {}),
    };
    for (const step of instance?.steps ?? []) {
      for (const item of flattenItems(step.items ?? [])) {
        if (
          item.content_key &&
          item.item_value !== undefined &&
          item.item_value !== null &&
          item.item_value !== ''
        ) {
          map[item.content_key] = item.item_value;
        }
      }
    }
    return map;
  }, [instance]);

  useEffect(() => {
    if (!embedInstanceId || !onEmbedComplete || !done || embedCompleteFired.current) return;
    embedCompleteFired.current = true;
    onEmbedComplete();
  }, [embedInstanceId, onEmbedComplete, done]);

  useEffect(() => {
    if (!done || !isSurveyFlow) return;
    void queryClient.invalidateQueries({ queryKey: ['surveys-my'] });
    void queryClient.invalidateQueries({ queryKey: ['surveys-available'] });
    // On completion, take the member to their submission results (not back to the start).
    if (flowResultsTo && !embedInstanceId) {
      navigate(flowResultsTo, { replace: true });
    }
  }, [done, isSurveyFlow, flowResultsTo, embedInstanceId, navigate, queryClient]);

  /* Seed local values from the instance whenever the active step (re)loads. */
  useEffect(() => {
    if (!activeStep) return;
    setValues((prev) => {
      const next = { ...prev };
      for (const item of flatItems) {
        if (item.item_value !== undefined && next[item.instance_item_id] === undefined) {
          next[item.instance_item_id] = item.item_value;
        }
      }
      return next;
    });
  }, [activeStep, flatItems]);

  /* A transition item drives the flow: persist answers, complete the step,
     advance — and finalise the instance once no active step remains. */
  const advance = useMutation({
    mutationFn: async () => {
      if (!instance || !activeStep) return undefined;
      const missingRequired = getMissingRequiredItems(flatItems, values);
      if (missingRequired.length > 0) {
        throw new Error(
          `Complete required fields before continuing: ${missingRequired
            .map((item) => item.name)
            .join(', ')}`,
        );
      }
      const invalidItems = flatItems
        .filter((item) => item.item_type === 'question' && item.is_available)
        .map((item) => ({
          item,
          messages: getClientValidationMessages(item, values[item.instance_item_id]),
        }))
        .filter(({ messages }) => messages.length > 0);
      if (invalidItems.length > 0) {
        throw new Error(invalidItems[0]?.messages[0] ?? 'Please review the highlighted fields.');
      }
      const questionItems = flatItems.filter(
        (i) => i.item_type === 'question' && i.is_available,
      );
      for (const item of questionItems) {
        const value = values[item.instance_item_id];
        if (value !== undefined && value !== '') {
          await updateItemValue(instance.instance_id, item.instance_item_id, value);
        }
      }
      await completeStep(instance.instance_id, activeStep.instance_step_id);

      // Activate the step immediately after the one just completed. We don't use
      // moveToNextStep here: completeStep clears the active step, and its no-active
      // fallback advances from the *highest* completed step — so after the user has
      // gone back, "Next" would skip ahead instead of going to the next step.
      const afterComplete = await getFlowInstance(instance.instance_id);
      const nextStep = (afterComplete.steps ?? [])
        .filter((s) => s.sequence_number > activeStep.sequence_number && s.is_available !== false)
        .sort((a, b) => a.sequence_number - b.sequence_number)[0];
      if (nextStep) {
        await activateStep(instance.instance_id, nextStep.instance_step_id);
        return getFlowInstance(instance.instance_id);
      }

      // No further step — finalize the instance once everything is done.
      const hasNavigableStep = afterComplete.steps?.some(
        (step) => step.status === 'active' || step.status === 'pending',
      );
      if (afterComplete.status !== 'completed' && !hasNavigableStep) {
        await completeInstance(instance.instance_id);
      }
      return getFlowInstance(instance.instance_id);
    },
    onSuccess: (refreshed) => {
      setError(null);
      if (refreshed) queryClient.setQueryData(queryKey, refreshed);
    },
    onError: (e) => {
      const apiError = toApiError(e);
      if (isMissingFlowResourceError(apiError.message)) {
        const title = 'This form session expired';
        const description = 'Please reopen the survey or eForm and try again.';
        setError(description);
        toast.warning(title, description);
        if (isSurveyFlow) handleSurveyBack();
        else navigate('/app/home', { replace: true });
        return;
      }
      setError(apiError.message);
    },
  });

  /* Step back to the previous step (server re-activates it; answers are kept). */
  const goBack = useMutation({
    mutationFn: async () => {
      if (!instance) return undefined;
      await moveToPreviousStep(instance.instance_id);
      return getFlowInstance(instance.instance_id);
    },
    onSuccess: (refreshed) => {
      setError(null);
      if (refreshed) queryClient.setQueryData(queryKey, refreshed);
    },
    onError: (e) => {
      const apiError = toApiError(e);
      if (isMissingFlowResourceError(apiError.message)) {
        const title = 'This form session expired';
        const description = 'Please reopen the survey or eForm and try again.';
        setError(description);
        toast.warning(title, description);
        if (isSurveyFlow) handleSurveyBack();
        else navigate('/app/home', { replace: true });
        return;
      }
      setError(apiError.message);
    },
  });

  if (instanceQuery.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-32" />
        <Skeleton className="h-11" />
      </div>
    );
  }
  if (instanceQuery.isError || !instance) {
    return <ErrorState onRetry={() => instanceQuery.refetch()} />;
  }

  const branding = instance.onboarding_branding;
  const compactSurveyTitle = resolveCompactSurveyTitle(
    flowTitle,
    instance.name,
    activeStep?.name,
    flowKind,
  );

  if (done) {
    if (onEmbedComplete) {
      return (
        <div className="flex flex-col items-center gap-4 py-6">
          <Skeleton className="h-8 w-48" />
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center gap-4 py-6 text-center">
        <CheckCircle2 className="h-12 w-12 text-success" strokeWidth={1.5} />
        <h1 className="display-serif text-display-lg text-ink">
          {branding?.title ?? "You're all set"}
        </h1>
        <p className="text-body text-ink-secondary">
          {branding?.message ?? (isSurveyFlow ? 'Your response has been submitted.' : 'Your onboarding is complete.')}
        </p>
        <ActionButton className="mt-2" onClick={() => (isSurveyFlow ? handleSurveyBack() : navigate('/app/home'))}>
          {isSurveyFlow ? 'Done' : 'Continue'}
        </ActionButton>
      </div>
    );
  }

  const items = [...flatItems]
    .filter((i) => i.is_available)
    .sort((a, b) => a.sequence_number - b.sequence_number);
  const transitionItems = items.filter((item) => item.item_type === 'transition');
  const nonTransitionItems = items.filter((item) => item.item_type !== 'transition');
  const missingRequiredItems = getMissingRequiredItems(nonTransitionItems, values);
  const validationMessagesByItemId = new Map(
    nonTransitionItems.map((item) => [
      item.instance_item_id,
      getClientValidationMessages(item, values[item.instance_item_id]),
    ]),
  );
  const hasBlockingValidationIssues = Array.from(validationMessagesByItemId.values()).some(
    (messages) => messages.length > 0,
  );
  const availableSteps = steps.filter((step) => step.is_available !== false);
  const isLastAvailableStep = Boolean(
    activeStep &&
      availableSteps[availableSteps.length - 1]?.instance_step_id === activeStep.instance_step_id,
  );

  const setValue = (id: string, v: unknown) => {
    setError(null);
    setValues((prev) => ({ ...prev, [id]: v }));
  };

  const nextDisabled = missingRequiredItems.length > 0 || hasBlockingValidationIssues;
  const nextLabel = isLastAvailableStep ? 'Complete' : 'Next';

  /* Survey / eForm: full-height layout — scrollable content with the
     Back / Next actions pinned to the bottom (no inline step buttons). */
  if (isSurveyFlow) {
    return (
      <div className="fixed inset-0 z-30 mx-auto flex max-w-md flex-col bg-paper">
        <CompactHeader
          title={compactSurveyTitle}
          onBack={handleSurveyBack}
          backLabel="Back to survey"
          className="shrink-0"
        />

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-4 pb-6">
          <div className="flex flex-col gap-5">
            {steps.length > 1 ? (
              <Stepper total={steps.length} current={stepIndex} labels={steps.map((s) => s.name)} />
            ) : null}

            <header>
              {branding?.title ? <span className="eyebrow">{branding.title}</span> : null}
              <h1 className="display-serif text-display-lg text-ink mt-1">
                {activeStep?.name ?? 'Welcome'}
              </h1>
              {activeStep?.description ? (
                <p className="text-body text-ink-secondary mt-1">{activeStep.description}</p>
              ) : null}
            </header>

            {error ? <Banner variant="danger" title={error} /> : null}

            <div className="flex flex-col gap-5">
              {nonTransitionItems.map((item) => (
                <OnboardingItem
                  key={item.instance_item_id}
                  item={item}
                  instanceId={instance.instance_id}
                  contentValues={contentValues}
                  value={values[item.instance_item_id]}
                  validationMessages={validationMessagesByItemId.get(item.instance_item_id) ?? []}
                  onChange={(v) => setValue(item.instance_item_id, v)}
                  onActionDone={() => queryClient.invalidateQueries({ queryKey })}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="shrink-0 border-t border-hairline bg-paper px-6 pt-4 pb-safe-or-8">
          {nextDisabled ? (
            <p className="mb-2 text-center text-caption text-ink-muted">
              {missingRequiredItems.length > 0
                ? 'Complete all required fields before continuing.'
                : 'Review the highlighted fields before continuing.'}
            </p>
          ) : null}
          <div className="flex gap-3">
            {stepIndex > 0 ? (
              <Button
                variant="secondary"
                size="lg"
                className="flex-1"
                disabled={advance.isPending || goBack.isPending}
                loading={goBack.isPending}
                onClick={() => goBack.mutate()}
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                Back
              </Button>
            ) : null}
            <Button
              variant="primary"
              size="lg"
              className="flex-1"
              disabled={nextDisabled || goBack.isPending}
              loading={advance.isPending}
              onClick={() => advance.mutate()}
            >
              {nextLabel}
              <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {isSurveyFlow ? (
        <div
          className="sticky top-0 z-20 overflow-hidden rounded-b-sm bg-primary-900 px-5 pt-safe-or-3 pb-3 backdrop-blur-xl backdrop-saturate-150 shadow-[inset_0_-1px_0_rgba(255,255,255,0.10),0_12px_30px_-18px_rgba(0,0,0,0.55)]"
          style={{
            marginLeft: '-1.5rem',
            marginRight: '-1.5rem',
            marginTop: 'calc(-1 * max(24px, env(safe-area-inset-top)))',
          }}
        >
          <div className="grid min-h-9 grid-cols-[2.25rem_minmax(0,1fr)] items-center gap-3">
            <Button
              type="button"
              onClick={handleSurveyBack}
              aria-label="Back to survey"
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-full border border-white/10 bg-white/12 text-white hover:bg-white/18"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
            </Button>
            <h1 className="min-w-0 truncate text-left font-display text-body font-semibold leading-6 text-white">
              {compactSurveyTitle}
            </h1>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleExit}
          className="self-end inline-flex items-center gap-1 rounded-pill px-2 py-1 text-caption text-ink-muted transition-colors hover:text-ink"
        >
          <X className="h-4 w-4" strokeWidth={1.75} />
          Exit
        </button>
      )}

      {steps.length > 1 ? (
        <Stepper total={steps.length} current={stepIndex} labels={steps.map((s) => s.name)} />
      ) : null}

      <header>
        {isSurveyFlow ? (
          branding?.title ? <span className="eyebrow">{branding.title}</span> : null
        ) : (
          <span className="eyebrow">{branding?.title ?? 'Onboarding'}</span>
        )}
        <h1 className="display-serif text-display-lg text-ink mt-1">
          {activeStep?.name ?? 'Welcome'}
        </h1>
        {activeStep?.description ? (
          <p className="text-body text-ink-secondary mt-1">{activeStep.description}</p>
        ) : null}
      </header>

      {error ? <Banner variant="danger" title={error} /> : null}

      <div className="flex flex-col gap-5">
        {nonTransitionItems.map((item) => {
          return (
            <OnboardingItem
              key={item.instance_item_id}
              item={item}
              instanceId={instance.instance_id}
              contentValues={contentValues}
              value={values[item.instance_item_id]}
              validationMessages={validationMessagesByItemId.get(item.instance_item_id) ?? []}
              onChange={(v) => setValue(item.instance_item_id, v)}
              onActionDone={() => queryClient.invalidateQueries({ queryKey })}
            />
          );
        })}

        {transitionItems.map((item) => {
          const c = cfg(item);
          const variant = c.button_style === 'secondary' ? 'secondary' : 'primary';
          return (
            <Button
              key={item.instance_item_id}
              width="full"
              variant={variant}
              disabled={missingRequiredItems.length > 0 || hasBlockingValidationIssues}
              loading={advance.isPending}
              onClick={() => advance.mutate()}
            >
              {str(c.button_label) ?? item.name ?? 'Continue'}
              <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          );
        })}

        {transitionItems.length === 0 ? (
          <Button
            width="full"
            disabled={missingRequiredItems.length > 0 || hasBlockingValidationIssues}
            loading={advance.isPending}
            onClick={() => advance.mutate()}
          >
            {isLastAvailableStep ? 'Submit' : 'Next step'}
            <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        ) : null}

        {stepIndex > 0 ? (
          <Button
            width="full"
            variant="ghost"
            disabled={advance.isPending || goBack.isPending}
            loading={goBack.isPending}
            onClick={() => goBack.mutate()}
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            Back
          </Button>
        ) : null}

        {missingRequiredItems.length > 0 ? (
          <p className="text-caption text-ink-muted">
            Complete all required fields before continuing.
          </p>
        ) : hasBlockingValidationIssues ? (
          <p className="text-caption text-ink-muted">
            Review the highlighted fields before continuing.
          </p>
        ) : null}
      </div>
    </div>
  );
}

function resolveCompactSurveyTitle(
  explicitTitle: string | undefined,
  instanceName: string | undefined,
  stepName: string | undefined,
  flowKind: string | undefined,
): string {
  const clean = (value: string | undefined): string => String(value ?? '').replace(/^Response:\s*/i, '').trim();

  const title = clean(explicitTitle) || clean(instanceName) || clean(stepName);
  if (title) return title;
  return flowKind === 'eform' ? 'eForm' : 'Survey';
}

function isMissingFlowResourceError(message: string | undefined): boolean {
  const normalized = String(message ?? '').toLowerCase();
  return normalized.includes('item not found') || normalized.includes('instance not found');
}

function getMissingRequiredItems(
  items: FlowInstanceStepItem[],
  values: Record<string, unknown>,
): FlowInstanceStepItem[] {
  return items.filter((item) => {
    if (item.is_available === false) return false;
    const config = cfg(item);
    const actionParams = asObject(config.action_params);
    const isRequired =
      config.is_required === true ||
      config.required === true ||
      actionParams.is_required === true ||
      actionParams.required === true;

    if (!isRequired) return false;
    return !isItemSatisfied(item, values[item.instance_item_id]);
  });
}

function getClientValidationMessages(item: FlowInstanceStepItem, draftValue: unknown): string[] {
  if (item.item_type !== 'question' || item.is_available === false) {
    return [];
  }

  const config = cfg(item);
  const rules = asObject(config.validation_rules);
  const inputType = String(config.input_type ?? config.type ?? 'text');
  const value = draftValue ?? item.item_value;

  if (value === null || value === undefined || value === '') {
    return [];
  }

  const messages: string[] = [];

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const minLength = asNumber(rules.min_length);
    const maxLength = asNumber(rules.max_length);
    const pattern = str(rules.pattern);

    if (typeof minLength === 'number' && trimmed.length < minLength) {
      messages.push(`Please enter at least ${minLength} characters.`);
    }
    if (typeof maxLength === 'number' && trimmed.length > maxLength) {
      messages.push(`Please keep this answer under ${maxLength} characters.`);
    }
    if (pattern) {
      try {
        const regex = new RegExp(pattern);
        if (!regex.test(trimmed)) {
          messages.push('Please match the required format.');
        }
      } catch {
        // Ignore invalid authored patterns on the client; the backend remains authoritative.
      }
    }
  }

  if (inputType === 'number' || typeof value === 'number') {
    const numericValue = typeof value === 'number' ? value : Number(value);
    if (!Number.isNaN(numericValue)) {
      const min = asNumber(rules.min);
      const max = asNumber(rules.max);
      if (typeof min === 'number' && numericValue < min) {
        messages.push(`Please enter a value of at least ${min}.`);
      }
      if (typeof max === 'number' && numericValue > max) {
        messages.push(`Please enter a value of ${max} or less.`);
      }
    }
  }

  return messages;
}

function isItemSatisfied(item: FlowInstanceStepItem, draftValue: unknown): boolean {
  if (item.item_type === 'content' || item.item_type === 'section' || item.item_type === 'transition') {
    return true;
  }

  if (item.item_type === 'action') {
    return item.status === 'completed';
  }

  if (item.status === 'completed') {
    return true;
  }

  const config = cfg(item);
  const inputType = String(config.input_type ?? config.type ?? 'text');
  const value = draftValue ?? item.item_value;

  if (inputType === 'checkbox' || inputType === 'boolean') {
    return typeof value === 'boolean';
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return value !== null && value !== undefined;
}

function asObject(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function getQuestionHelperText(item: FlowInstanceStepItem, value: unknown): string | undefined {
  const rules = asObject(cfg(item).validation_rules);
  const minLength = asNumber(rules.min_length);
  const maxLength = asNumber(rules.max_length);
  if (typeof minLength !== 'number' && typeof maxLength !== 'number') {
    return undefined;
  }

  const length = typeof value === 'string' ? value.trim().length : 0;
  if (typeof minLength === 'number' && typeof maxLength === 'number') {
    return length > 0
      ? `${length} characters entered. Keep between ${minLength} and ${maxLength} characters.`
      : `Enter between ${minLength} and ${maxLength} characters.`;
  }
  if (typeof minLength === 'number') {
    return length > 0
      ? `${length} / ${minLength} characters minimum`
      : `Enter at least ${minLength} characters.`;
  }
  return length > 0
    ? `${length} / ${maxLength} characters`
    : `Keep this answer under ${maxLength} characters.`;
}

/* ── Item dispatcher ─────────────────────────────────────────────────── */

function OnboardingItem({
  item,
  instanceId,
  contentValues,
  value,
  validationMessages,
  onChange,
  onActionDone,
}: {
  item: FlowInstanceStepItem;
  instanceId: string;
  contentValues: Record<string, unknown>;
  value: unknown;
  validationMessages: string[];
  onChange: (value: unknown) => void;
  onActionDone: () => void;
}): JSX.Element | null {
  if (item.item_type === 'content' || item.item_type === 'section') {
    return <ContentItem item={item} values={contentValues} />;
  }
  if (item.item_type === 'action') {
    return <ActionItem item={item} instanceId={instanceId} onDone={onActionDone} />;
  }
  if (item.item_type === 'question') {
    return (
      <QuestionItem
        item={item}
        instanceId={instanceId}
        value={value}
        validationMessages={validationMessages}
        onChange={onChange}
      />
    );
  }
  return null;
}

/* ── Content ─────────────────────────────────────────────────────────── */

/** Render a stored value for display inside content (arrays joined, booleans as Yes/No). */
function formatContentValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(formatContentValue).filter(Boolean).join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

/** Replace `{{content_key}}` placeholders with the member's answers. Unknown keys clear out. */
function interpolateContent(body: string, values: Record<string, unknown>): string {
  return body.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_match, key: string) =>
    key in values ? formatContentValue(values[key]) : '',
  );
}

function ContentItem({
  item,
  values,
}: {
  item: FlowInstanceStepItem;
  values: Record<string, unknown>;
}): JSX.Element | null {
  const c = cfg(item);
  const rawBody = str(c.content_data) ?? str(c.body) ?? str(c.content);
  const body = rawBody ? interpolateContent(rawBody, values) : rawBody;
  const isHtml = c.content_type === 'html';

  if (!body) {
    return item.name ? <p className="font-display text-headline text-ink">{item.name}</p> : null;
  }
  if (isHtml) {
    return (
      <div
        className="onboarding-content text-left text-body text-ink-secondary [&_*]:!text-left [&_[style*='inline-block']]:!block [&_h1]:font-display [&_h1]:text-display-lg [&_h1]:text-ink [&_h2]:font-display [&_h2]:text-headline [&_h2]:text-ink [&_h3]:font-display [&_h3]:text-headline [&_h3]:text-ink [&_p]:mt-1.5 [&_a]:text-primary-700 [&_ul]:list-disc [&_ul]:pl-5"
        // Content is authored in the tenant's flow template (trusted admin input).
        dangerouslySetInnerHTML={{ __html: body }}
      />
    );
  }
  return <p className="text-body text-ink-secondary whitespace-pre-line">{body}</p>;
}

/* ── Question ────────────────────────────────────────────────────────── */

function QuestionItem({
  item,
  instanceId,
  value,
  validationMessages,
  onChange,
}: {
  item: FlowInstanceStepItem;
  instanceId: string;
  value: unknown;
  validationMessages: string[];
  onChange: (value: unknown) => void;
}): JSX.Element {
  const c = cfg(item);
  const inputType = String(c.input_type ?? c.type ?? 'text');
  const placeholder = str(c.placeholder);
  const required = c.is_required === true;
  const options = Array.isArray(c.options)
    ? (c.options as Array<{ value: string; label: string }>)
    : [];
  const fieldId = `flow-${item.instance_item_id}`;
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const validationMessage = validationMessages[0];
  const helperText = getQuestionHelperText(item, value);

  const inputClass =
    `rounded-sm bg-card hairline px-3 py-2.5 text-body text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
      validationMessage ? 'border border-danger/50 focus-visible:ring-danger' : ''
    }`;

  const label = (
    <span className="text-body-sm font-medium text-ink">
      {item.name}
      {required ? <span className="text-danger"> *</span> : null}
    </span>
  );

  /* ── File / image upload ───────────────────────────────────────────── */
  if (inputType === 'file' || inputType === 'image') {
    const isImage = inputType === 'image';
    // Images are single by default; files may allow multiple via config.
    const allowMultiple = !isImage && c.allow_multiple === true;
    const urls = Array.isArray(value)
      ? (value as string[])
      : typeof value === 'string' && value
        ? [value]
        : [];
    const count = urls.length;
    const accept = isImage ? 'image/*' : undefined;
    const showPicker = allowMultiple || count === 0;

    const removeAt = (idx: number) => {
      const next = urls.filter((_, i) => i !== idx);
      onChange(allowMultiple ? next : (next[0] ?? ''));
    };

    const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (!files.length) return;
      setUploading(true);
      setUploadError(null);
      try {
        const uploaded = await uploadFlowFiles(instanceId, files);
        if (allowMultiple) onChange([...urls, ...uploaded]);
        else if (uploaded[0]) onChange(uploaded[0]);
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setUploading(false);
        e.target.value = '';
      }
    };

    return (
      <div className="flex flex-col gap-1.5">
        {label}

        {/* Image previews */}
        {isImage && count > 0 ? (
          <div className="flex flex-wrap gap-2">
            {urls.map((url, idx) => (
              <div key={url + idx} className="relative">
                <img src={url} alt={item.name} className="h-24 w-24 rounded-md object-cover hairline" />
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  aria-label="Remove photo"
                  className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-ink text-paper shadow-sm"
                >
                  <X className="h-3.5 w-3.5" strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        ) : null}

        {/* File previews (non-image) */}
        {!isImage && count > 0 ? (
          <div className="flex flex-col gap-1.5">
            {urls.map((url, idx) => (
              <div key={url + idx} className="flex items-center gap-2 rounded-sm bg-card hairline px-3 py-2 text-body-sm">
                <Paperclip className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.75} />
                <a href={url} target="_blank" rel="noreferrer" className="truncate text-primary-700 underline">
                  View file {urls.length > 1 ? idx + 1 : ''}
                </a>
                <button type="button" onClick={() => removeAt(idx)} aria-label="Remove file" className="ml-auto text-ink-muted">
                  <X className="h-4 w-4" strokeWidth={1.75} />
                </button>
              </div>
            ))}
          </div>
        ) : null}

        {/* Picker — shown when nothing is chosen yet (single) or always (multiple). */}
        {showPicker ? (
          <label
            htmlFor={fieldId}
            className="flex cursor-pointer items-center gap-2 rounded-sm border border-dashed border-hairline-strong bg-elevated px-3 py-3 text-body-sm text-ink-secondary"
          >
            {isImage ? (
              <ImageIcon className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.75} />
            ) : (
              <Paperclip className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.75} />
            )}
            <span className="truncate">
              {uploading
                ? 'Uploading…'
                : placeholder ?? (isImage ? 'Choose a photo' : allowMultiple ? 'Add a file' : 'Choose a file')}
            </span>
            <input id={fieldId} type="file" accept={accept} multiple={allowMultiple} className="hidden" onChange={handleFiles} />
          </label>
        ) : (
          // Single image already chosen — offer a clear Replace action.
          <label htmlFor={fieldId} className="cursor-pointer text-body-sm font-medium text-primary-700">
            {uploading ? 'Uploading…' : 'Replace photo'}
            <input id={fieldId} type="file" accept={accept} className="hidden" onChange={handleFiles} />
          </label>
        )}

        {uploadError ? <span className="text-caption text-danger">{uploadError}</span> : null}
        {!uploadError && validationMessage ? (
          <span className="text-caption text-danger">{validationMessage}</span>
        ) : helperText ? (
          <span className="text-caption text-ink-muted">{helperText}</span>
        ) : null}
      </div>
    );
  }

  /* ── Radio (single choice) ─────────────────────────────────────────── */
  if (inputType === 'radio' && options.length > 0) {
    const current = String(value ?? '');
    return (
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-body-sm font-medium text-ink">
          {item.name}
          {required ? <span className="text-danger"> *</span> : null}
        </legend>
        <div className="flex flex-col gap-2">
          {options.map((o) => (
            <label
              key={o.value}
              className="flex items-center gap-2 rounded-sm bg-card hairline px-3 py-2.5 text-body-sm text-ink"
            >
              <input
                type="radio"
                name={fieldId}
                value={o.value}
                checked={current === o.value}
                onChange={() => onChange(o.value)}
                className="h-4 w-4 accent-[color:var(--primary-600)]"
              />
              {o.label}
            </label>
          ))}
        </div>
        {validationMessage ? (
          <span className="text-caption text-danger">{validationMessage}</span>
        ) : helperText ? (
          <span className="text-caption text-ink-muted">{helperText}</span>
        ) : null}
      </fieldset>
    );
  }

  /* ── Multiselect / Tag box (multiple choice → string[]) ────────────── */
  if ((inputType === 'multiselect' || inputType === 'tagbox') && options.length > 0) {
    const selected = Array.isArray(value) ? (value as string[]) : [];
    const toggle = (v: string) =>
      onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
    return (
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-body-sm font-medium text-ink">
          {item.name}
          {required ? <span className="text-danger"> *</span> : null}
        </legend>
        <div className="flex flex-col gap-2">
          {options.map((o) => (
            <label
              key={o.value}
              className="flex items-center gap-2 rounded-sm bg-card hairline px-3 py-2.5 text-body-sm text-ink"
            >
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={() => toggle(o.value)}
                className="h-4 w-4 accent-[color:var(--primary-600)]"
              />
              {o.label}
            </label>
          ))}
        </div>
        {validationMessage ? (
          <span className="text-caption text-danger">{validationMessage}</span>
        ) : helperText ? (
          <span className="text-caption text-ink-muted">{helperText}</span>
        ) : null}
      </fieldset>
    );
  }

  /* ── Rating / NPS (numeric scale) ──────────────────────────────────── */
  if (inputType === 'rating') {
    const min = Number.isFinite(Number(c.rate_min)) ? Number(c.rate_min) : 0;
    const max = Number.isFinite(Number(c.rate_max)) ? Number(c.rate_max) : 10;
    const range: number[] = [];
    for (let n = min; n <= max; n++) range.push(n);
    const current = typeof value === 'number' ? value : value != null && value !== '' ? Number(value) : null;
    return (
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-body-sm font-medium text-ink">
          {item.name}
          {required ? <span className="text-danger"> *</span> : null}
        </legend>
        <div className="flex flex-wrap gap-2">
          {range.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              className={`h-10 w-10 rounded-sm border text-body-sm ${
                current === n
                  ? 'border-primary-600 bg-primary-600 text-white'
                  : 'border-hairline-strong bg-card text-ink'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
        {str(c.min_label) || str(c.max_label) ? (
          <div className="flex justify-between text-caption text-ink-muted">
            <span>{str(c.min_label)}</span>
            <span>{str(c.max_label)}</span>
          </div>
        ) : null}
        {validationMessage ? (
          <span className="text-caption text-danger">{validationMessage}</span>
        ) : helperText ? (
          <span className="text-caption text-ink-muted">{helperText}</span>
        ) : null}
      </fieldset>
    );
  }

  /* ── Image picker (choose from preset images) ──────────────────────── */
  if (inputType === 'imagepicker') {
    const choices = Array.isArray(c.image_choices)
      ? (c.image_choices as Array<{ label: string; value: string; image_url: string }>)
      : [];
    const current = String(value ?? '');
    return (
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-body-sm font-medium text-ink">
          {item.name}
          {required ? <span className="text-danger"> *</span> : null}
        </legend>
        <div className="flex flex-wrap gap-3">
          {choices.map((choice) => (
            <button
              key={String(choice.value)}
              type="button"
              onClick={() => onChange(choice.value)}
              className={`rounded-sm border p-1 ${
                current === String(choice.value) ? 'border-primary-600' : 'border-hairline-strong'
              }`}
            >
              <img src={choice.image_url} alt={choice.label} className="h-24 w-24 rounded object-cover" />
              <span className="mt-1 block text-center text-caption text-ink-secondary">{choice.label}</span>
            </button>
          ))}
        </div>
        {validationMessage ? (
          <span className="text-caption text-danger">{validationMessage}</span>
        ) : helperText ? (
          <span className="text-caption text-ink-muted">{helperText}</span>
        ) : null}
      </fieldset>
    );
  }

  /* ── Ranking (order the options → value[]) ─────────────────────────── */
  if (inputType === 'ranking' && options.length > 0) {
    const order: string[] =
      Array.isArray(value) && value.length ? (value as string[]).map(String) : options.map((o) => String(o.value));
    for (const o of options) {
      if (!order.includes(String(o.value))) order.push(String(o.value));
    }
    const labelFor = (v: string) => options.find((o) => String(o.value) === v)?.label ?? v;
    const move = (idx: number, dir: -1 | 1) => {
      const next = [...order];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return;
      const a = next[idx];
      const b = next[target];
      if (a === undefined || b === undefined) return;
      next[idx] = b;
      next[target] = a;
      onChange(next);
    };
    return (
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-body-sm font-medium text-ink">
          {item.name}
          {required ? <span className="text-danger"> *</span> : null}
        </legend>
        <div className="flex flex-col gap-2">
          {order.map((v, idx) => (
            <div
              key={v}
              className="flex items-center justify-between rounded-sm bg-card hairline px-3 py-2.5 text-body-sm text-ink"
            >
              <span>
                <span className="mr-2 text-ink-muted">{idx + 1}.</span>
                {labelFor(v)}
              </span>
              <span className="flex gap-1">
                <button
                  type="button"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  className="px-1 text-ink-muted disabled:opacity-40"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => move(idx, 1)}
                  disabled={idx === order.length - 1}
                  className="px-1 text-ink-muted disabled:opacity-40"
                >
                  ↓
                </button>
              </span>
            </div>
          ))}
        </div>
        {validationMessage ? (
          <span className="text-caption text-danger">{validationMessage}</span>
        ) : helperText ? (
          <span className="text-caption text-ink-muted">{helperText}</span>
        ) : null}
      </fieldset>
    );
  }

  /* ── Multiple text fields (object keyed by field_key) ──────────────── */
  if (inputType === 'multitext') {
    const fields = Array.isArray(c.form_fields)
      ? (c.form_fields as Array<{ field_key: string; field_label: string; placeholder?: string }>)
      : [];
    const obj = value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
    const setField = (key: string, v: string) => onChange({ ...obj, [key]: v });
    return (
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-body-sm font-medium text-ink">
          {item.name}
          {required ? <span className="text-danger"> *</span> : null}
        </legend>
        <div className="flex flex-col gap-2">
          {fields.map((f) => (
            <label key={f.field_key} className="flex flex-col gap-1">
              <span className="text-caption text-ink-secondary">{f.field_label}</span>
              <input
                type="text"
                value={String(obj[f.field_key] ?? '')}
                onChange={(e) => setField(f.field_key, e.target.value)}
                placeholder={f.placeholder ?? ''}
                className={inputClass}
              />
            </label>
          ))}
        </div>
        {validationMessage ? (
          <span className="text-caption text-danger">{validationMessage}</span>
        ) : helperText ? (
          <span className="text-caption text-ink-muted">{helperText}</span>
        ) : null}
      </fieldset>
    );
  }

  /* ── Signature (canvas → uploaded image reference) ─────────────────── */
  if (inputType === 'signature') {
    const signed = typeof value === 'string' && value;
    return (
      <div className="flex flex-col gap-1.5">
        {label}
        <SignatureField
          disabled={uploading}
          onCapture={async (file) => {
            if (!file) {
              onChange('');
              return;
            }
            setUploading(true);
            setUploadError(null);
            try {
              const uploaded = await uploadFlowFiles(instanceId, [file]);
              if (uploaded[0]) onChange(uploaded[0]);
            } catch (err) {
              setUploadError(err instanceof Error ? err.message : 'Upload failed');
            } finally {
              setUploading(false);
            }
          }}
        />
        {uploading ? <span className="text-caption text-ink-muted">Saving signature…</span> : null}
        {signed && !uploading ? <span className="text-caption text-success">Signature captured.</span> : null}
        {uploadError ? (
          <span className="text-caption text-danger">{uploadError}</span>
        ) : validationMessage ? (
          <span className="text-caption text-danger">{validationMessage}</span>
        ) : helperText ? (
          <span className="text-caption text-ink-muted">{helperText}</span>
        ) : null}
      </div>
    );
  }

  return (
    <label htmlFor={fieldId} className="flex flex-col gap-1.5">
      {label}

      {inputType === 'textarea' ? (
        <textarea
          id={fieldId}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          placeholder={placeholder}
          className={inputClass}
        />
      ) : (inputType === 'select' || inputType === 'dropdown') && options.length > 0 ? (
        <Dropdown
          id={fieldId}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{placeholder ?? 'Select…'}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Dropdown>
      ) : inputType === 'checkbox' || inputType === 'boolean' ? (
        <span className="flex items-center gap-2">
          <input
            id={fieldId}
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            className="h-4 w-4 accent-[color:var(--primary-600)]"
          />
          <span className="text-body-sm text-ink-secondary">
            {str(c.checkbox_label) ?? 'Yes'}
          </span>
        </span>
      ) : (
        <input
          id={fieldId}
          type={
            inputType === 'email'
              ? 'email'
              : inputType === 'phone' || inputType === 'tel'
                ? 'tel'
                : inputType === 'number'
                  ? 'number'
                  : inputType === 'date'
                    ? 'date'
                    : inputType === 'datetime'
                      ? 'datetime-local'
                      : 'text'
          }
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={inputClass}
        />
      )}

      {validationMessage ? (
        <span className="text-caption text-danger">{validationMessage}</span>
      ) : helperText ? (
        <span className="text-caption text-ink-muted">{helperText}</span>
      ) : null}
    </label>
  );
}

/* ── Action ──────────────────────────────────────────────────────────── */

function ActionItem({
  item,
  instanceId,
  onDone,
}: {
  item: FlowInstanceStepItem;
  instanceId: string;
  onDone: () => void;
}): JSX.Element {
  const c = cfg(item);
  const actionType = String(c.action_type ?? c.action_handler ?? '');

  if (actionType === 'otpverify') {
    return <OtpAction item={item} instanceId={instanceId} onDone={onDone} />;
  }

  // Generic action — surfaced so the step isn't silently missing an item.
  return (
    <Card noGrain className="rounded-xl p-4">
      <p className="text-body-sm font-medium text-ink">{item.name}</p>
      <p className="mt-1 text-caption text-ink-muted">This step is handled by the estate office.</p>
    </Card>
  );
}

function OtpAction({
  item,
  instanceId,
  onDone,
}: {
  item: FlowInstanceStepItem;
  instanceId: string;
  onDone: () => void;
}): JSX.Element {
  const c = cfg(item);
  const params = (c.action_params ?? {}) as Config;
  const [token, setToken] = useState('');
  const [code, setCode] = useState('');
  const [phase, setPhase] = useState<'idle' | 'sent' | 'verified'>(
    item.status === 'completed' ? 'verified' : 'idle',
  );
  const [err, setErr] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: () => requestFlowOtp(instanceId, item.instance_item_id),
    onSuccess: (res) => {
      setToken(String(res.token ?? ''));
      setPhase('sent');
      setErr(null);
    },
    onError: (e) => setErr(toApiError(e).message),
  });

  const verify = useMutation({
    mutationFn: () => verifyFlowOtp(instanceId, item.instance_item_id, token, code.trim()),
    onSuccess: () => {
      setPhase('verified');
      setErr(null);
      onDone();
    },
    onError: (e) => setErr(toApiError(e).message),
  });

  return (
    <Card noGrain className="rounded-xl p-4">
      <p className="text-body-sm font-medium text-ink">{item.name}</p>
      {str(params.help_text) ? (
        <p className="mt-1 text-caption text-ink-muted">{String(params.help_text)}</p>
      ) : null}

      {phase === 'verified' ? (
        <p className="mt-3 flex items-center gap-2 text-body-sm text-success">
          <CheckCircle2 className="h-4 w-4" strokeWidth={1.75} />
          Verified
        </p>
      ) : phase === 'sent' ? (
        <div className="mt-3 flex flex-col gap-2.5">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder={str(params.code_label) ?? 'Verification code'}
            className="rounded-sm bg-elevated hairline px-3 py-2.5 text-body text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          />
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => send.mutate()}
              loading={send.isPending}
            >
              {str(params.send_button_label) ?? 'Resend code'}
            </Button>
            <Button
              width="full"
              disabled={code.trim().length < 4}
              loading={verify.isPending}
              onClick={() => verify.mutate()}
            >
              {str(params.verify_button_label) ?? 'Verify code'}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          className="mt-3"
          variant="secondary"
          loading={send.isPending}
          onClick={() => send.mutate()}
        >
          {str(params.send_button_label) ?? 'Send code'}
        </Button>
      )}

      {err ? <p className="mt-2 text-caption text-danger">{err}</p> : null}
    </Card>
  );
}

/**
 * Backwards-compatible onboarding entry point: the post-login onboarding gate and
 * the access-invite embed render this. Surveys/eForms use the dedicated SurveyRunner.
 */
export function Onboarding(props: Omit<OnboardingEmbedProps, 'variant'> = {}): JSX.Element {
  return <FlowRunner {...props} variant="onboarding" />;
}
